import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import FlexBetween from "shared/FlexBetween";
import Header from "shared/Header";
import {
  Box,
  useTheme,
  Button,
  Typography,
  Modal,
  IconButton,
  Tab,
  Tabs,
} from "@mui/material";
import axios from "axios";
import "react-toastify/dist/ReactToastify.css";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { indigo } from "@mui/material/colors";
import PublishIcon from "@mui/icons-material/Publish";

import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import CloseIcon from "@mui/icons-material/Close";
import MenuItem from "@mui/material/MenuItem";
import { useNavigate } from "react-router-dom";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import DeleteIcon from "@mui/icons-material/Delete";
import { ToastContainer, toast } from "react-toastify";
import ConfirmationDialog from "../../../shared/ConfirmationDialog";
import {
  getCalendarPdfUrl,
  dashboardStyle,
  tabStyles,
  pageContainerStyle,
  tableContainerStyle,
} from "shared/utils";
import PayslipTable from "../employee-payslips/PayslipTable";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import Spinner from "shared/spinner/Spinner";
import useAWSDetails from "shared/useAWSDetails";

function ManagePayslips() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [selectedName, setSelectedName] = useState("");
  const [employees, setEmployees] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [selectedYear, setSelectedYear] = useState(2024);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedPayslips, setSelectedPayslips] = useState([]);
  const [open, setOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [selectedPayslipId, setSelectedPayslipId] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { fetchedBucketName, fetchedRegion } = useAWSDetails();

  const fetchPayslips = () => {
    axios
      .get(`${process.env.REACT_APP_API_URL}payslips/getpayslips`)
      .then((response) => {
        setPayslips(response.data);
        setEmployeeId(response.data[0].employeeId);
      })
      .catch((error) => console.error("Error fetching payslips:", error));
  };

  useEffect(() => {
    fetchPayslips();
  }, []);

  useEffect(() => {
    axios
      .get(process.env.REACT_APP_API_URL)
      .then((result) => setUsers(result.data))
      .catch((err) => console.log(err));
  }, []);

  const handleChange = (event) => {
    const selectedValue = event.target.value;
    setSelectedName(selectedValue === "" ? "" : selectedValue);
  };

  useEffect(() => {
    axios
      .get(process.env.REACT_APP_API_URL)
      .then((response) => {
        setEmployees(response.data);
      })
      .catch((error) => {
        console.error("Error fetching user emails:", error);
      });
  }, []);

  const handleConfirmDelete = () => {
    setIsLoading(true);
    axios
      .delete(
        `${process.env.REACT_APP_API_URL}payslips/deletepayslip/${selectedPayslipId}`
      )
      .then((response) => {
        toast.success("Payslip deleted successfully:", response.data);
        setPayslips(
          payslips.filter((payslip) => payslip._id !== selectedPayslipId)
        );
        fetchPayslips();
      })
      .catch((error) => {
        console.error("Error deleting payslip:", error);
        setIsLoading(false);
      })
      .finally(() => {
        setConfirmationOpen(false);
        setIsLoading(false);
      });
  };

  const handleDeleteConfirmation = (payslipId) => {
    setSelectedPayslipId(payslipId);
    setConfirmationOpen(true);
  };

  const handleDeleteCancel = () => {
    setConfirmationOpen(false);
  };

  const payslipsRows = () => {
    const rows = payslips
      .reverse()
      .flatMap((payslip, index) =>
        payslip.payslips.flatMap((innerPayslip, innerIndex) => ({
          id: `${index}-${innerIndex}`,
          newId: innerPayslip._id,
          employeeId: payslip.employeeId,
          name: payslip.name,
          year: innerPayslip.year,
          month: innerPayslip.month,
          filename: innerPayslip.filename,
          createdOn: innerPayslip.createdOn,
          updatedOn: innerPayslip.updatedOn,
          status: innerPayslip.status,
        }))
      )
      .filter((payslip) => {
        let matchName = true;
        if (selectedName !== "") {
          matchName = payslip.name === selectedName;
        }
        return matchName;
      });

    setSelectedPayslips(rows);
  };

  const handleOpen = (url) => {
    setPdfUrl(url);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  useEffect(() => {
    payslipsRows();
  }, [selectedName, payslips]);

  const lightTheme = createTheme({
    palette: {
      primary: indigo,
    },
  });

  const years = Array.from({ length: 11 }, (_, index) => 2024 + index);
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const handleMonthChange = (event) => {
    setSelectedMonth(event.target.value);
  };

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
  };

  const columns = [
    {
      field: "employeeId",
      headerName: "Employee ID",
      flex: 0.5,
      renderHeader: () => (
        <Typography
 sx={{ fontSize: "14px", fontWeight: "bold" }}>
          Emp Id
        </Typography>
      ),
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      renderHeader: () => (
        <Typography
 sx={{ fontSize: "14px", fontWeight: "bold" }}>
          Name
        </Typography>
      ),
    },
    {
      field: "year",
      headerName: "Year",
      flex: 1,
      renderHeader: () => (
        <Typography
 sx={{ fontSize: "14px", fontWeight: "bold" }}>
          Year
        </Typography>
      ),
    },
    {
      field: "month",
      headerName: "Month",
      flex: 1,
      renderHeader: () => (
        <Typography
 sx={{ fontSize: "14px", fontWeight: "bold" }}>
          Month
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderHeader: () => (
        <Typography
 sx={{ fontSize: "14px", fontWeight: "bold" }}>
          Status
        </Typography>
      ),
    },
    {
      field: "updatedOn",
      headerName: "Updated On",
      flex: 1,
      renderHeader: () => (
        <Typography
 sx={{ fontSize: "14px", fontWeight: "bold" }}>
          Updated On
        </Typography>
      ),
    },
    {
      field: "view",
      headerName: "View",
      flex: 1,
      renderHeader: () => (
        <Typography
 sx={{ fontSize: "14px", fontWeight: "bold" }}>
          View
        </Typography>
      ),
      renderCell: (params) => (
        <div>
          <IconButton
            onClick={() =>
              handleOpen(
                `https://${fetchedBucketName}.s3.${fetchedRegion}.amazonaws.com/${getCalendarPdfUrl()}/employees/${
                  params.row.employeeId
                }/payslips/${params.row.filename}`
              )
            }
            sx={{ color: "#04497a" }}
          >
            <RemoveRedEyeIcon />
          </IconButton>
        </div>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      renderHeader: () => (
        <Typography
 sx={{ fontSize: "14px", fontWeight: "bold" }}>
          Action
        </Typography>
      ),
      renderCell: (params) => (
        <div>
          <DeleteIcon
            style={{ cursor: "pointer", color: "#04497a", fontSize: "25px" }}
            onClick={() => handleDeleteConfirmation(params.row.newId)}
          />
        </div>
      ),
    },
  ];

  const handleFilterClick = () => {
    const filteredPayslips = payslips.flatMap((payslip) =>
      payslip.payslips
        .filter((innerPayslip) => {
          let matchYear = true;
          let matchMonth = true;

          if (selectedYear !== "") {
            matchYear = innerPayslip.year === selectedYear;
          }

          if (selectedMonth) {
            matchMonth = innerPayslip.month === selectedMonth;
          }

          return matchYear && matchMonth;
        })
        .map((innerPayslip, innerIndex) => ({
          id: `${payslip.employeeId}-${innerIndex}`,
          employeeId: payslip.employeeId,
          name: payslip.name,
          year: innerPayslip.year,
          month: innerPayslip.month,
          status: innerPayslip.status,
        }))
    );

    setSelectedPayslips(filteredPayslips);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const styles = tabStyles(theme, selectedTab);

  return (
    <div>
      {isLoading && <Spinner />}

      {/* PDF Modal */}
      <ThemeProvider theme={lightTheme}>
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "white",
              boxShadow: 24,
              padding: "20px",
              borderRadius: "8px",
              width: "80%",
              height: "90%",
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography id="modal-title" variant="h6" component="h2">
              View Payslip
            </Typography>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                position: "absolute",
                top: 0,
                right: 0,
                color: "black",
              }}
            >
              <CloseIcon />
            </IconButton>
            <iframe
              src={pdfUrl}
              width="95%"
              height="90%"
              title="PDF viewer"
              style={{ margin: "auto" }}
            />
          </Box>
        </Modal>
      </ThemeProvider>

      {/* Main Page Container */}
      <Box sx={pageContainerStyle}>
        {/* Tabs Section */}
        <Box>
          <ThemeProvider theme={lightTheme}>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                marginBottom: "20px",
                height: "60px",
                color: "white",
                borderBottom: "1px solid #e5e5e5",
              }}
            >
              <Tab
                label="Manage Employee Payslips"
                icon={<ManageAccountsOutlinedIcon sx={{ marginRight: "4px" }} />}
                sx={styles}
              />
              <Tab
                label="Manage Personal Payslips"
                icon={<GroupsOutlinedIcon sx={{ marginRight: "4px" }} />}
                sx={styles}
              />
            </Tabs>
          </ThemeProvider>
        </Box>

        {/* Tab 0: Manage Employee Payslips */}
        {selectedTab === 0 && (
          <Box sx={tableContainerStyle}>
            {/* Header Row */}
            <FlexBetween sx={{ marginBottom: "15px" }}>
              <Header title="Manage Payslips" />
              <Button
                variant="contained"
                onClick={() => navigate("/uploadpayslips")}
                sx={{
                  backgroundColor: theme.palette.secondary.light,
                  color: theme.palette.background.alt,
                  fontSize: "14px",
                  fontWeight: "bolder",
                  padding: "10px 20px",
                }}
              >
                <PublishIcon sx={{ mr: "10px" }} /> Upload Payslip
              </Button>
            </FlexBetween>

            {/* Filters Row */}
            <ThemeProvider theme={lightTheme}>
              <FlexBetween sx={{ justifyContent: "flex-start", marginBottom: "15px" }}>
                <FormControl sx={{ minWidth: "150px" }}>
                  <InputLabel id="demo-simple-select-label">Name</InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={selectedName}
                    label="Name"
                    onChange={handleChange}
                  >
                    <MenuItem value="">Show All</MenuItem>
                    {employees.map((employee) => (
                      <MenuItem key={employee._id} value={employee.name}>
                        {employee.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl variant="standard" sx={{ minWidth: "120px", ml: "15px" }}>
                  <InputLabel id="year-select-label">Year</InputLabel>
                  <Select
                    labelId="year-select-label"
                    id="year-select"
                    label="Year"
                    value={selectedYear}
                    onChange={handleYearChange}
                  >
                    {years.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl variant="standard" sx={{ minWidth: "120px", ml: "15px" }}>
                  <InputLabel id="month-select-label">Month</InputLabel>
                  <Select
                    labelId="month-select-label"
                    id="month-select"
                    label="Month"
                    value={selectedMonth}
                    onChange={handleMonthChange}
                    sx={{ color: theme.palette.background.text }}
                  >
                    <MenuItem value="">
                      <em>Show All</em>
                    </MenuItem>
                    {months.map((month, index) => (
                      <MenuItem key={index + 1} value={month}>
                        {month}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  onClick={handleFilterClick}
                  sx={{
                    backgroundColor: theme.palette.secondary.light,
                    color: theme.palette.background.alt,
                    fontSize: "14px",
                    fontWeight: "bolder",
                    padding: "10px 20px",
                    ml: "15px",
                  }}
                >
                  Filter
                </Button>
              </FlexBetween>
            </ThemeProvider>

            {/* Table - Takes remaining space */}
            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
              <ThemeProvider theme={lightTheme}>
                <DataGrid
                  rows={selectedPayslips}
                  columns={columns}
                  initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
                  pageSizeOptions={[5, 10]}
                  sx={dashboardStyle(theme)}
                />
              </ThemeProvider>
            </Box>

            <ConfirmationDialog
              open={confirmationOpen}
              handleClose={handleDeleteCancel}
              handleConfirm={handleConfirmDelete}
              message="Are you sure you want to delete this payslip record?"
            />
            <ToastContainer />
          </Box>
        )}

        {/* Tab 1: Personal Payslips */}
        {selectedTab === 1 && (
          <Box sx={tableContainerStyle}>
            <PayslipTable />
          </Box>
        )}
      </Box>
    </div>
  );
}

export default ManagePayslips;